import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { LANDING } from "@/constants/testIds";
import { fetchConfig, getCheckoutStatus } from "@/lib/api";

export default function ThankYouPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paymentId = params.get("payment_id");
  const urlStatus = params.get("status");
  const [state, setState] = useState({ status: "checking", payment_status: "pending", downloads: [], email: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: "missing" }));
      return;
    }

    if (urlStatus === "failed" || urlStatus === "cancelled") {
      setState((s) => ({ ...s, status: "failed", payment_status: "failed" }));
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId, { paymentId });
        setState({
          status: data.status,
          payment_status: data.payment_status,
          downloads: data.downloads || [],
          email: data.email,
        });
        if (data.payment_status === "paid") return;
        if (data.payment_status === "failed") return;
        if (data.status === "expired") return;
        if (attemptsRef.current >= 8) {
          if (data.payment_status !== "paid") {
            setState((s) => ({ ...s, status: "timeout" }));
          }
          return;
        }
        attemptsRef.current += 1;
        timer = setTimeout(poll, 2000);
      } catch (e) {
        if (attemptsRef.current >= 8) {
          setState((s) => ({ ...s, status: "error" }));
          return;
        }
        attemptsRef.current += 1;
        timer = setTimeout(poll, 2000);
      }
    };
    fetchConfig().then(poll).catch(() => poll());
    return () => timer && clearTimeout(timer);
  }, [sessionId, paymentId, urlStatus]);

  const isPaid = state.payment_status === "paid";
  const isFailed = state.payment_status === "failed" || state.status === "failed" || state.status === "error" || state.status === "timeout";

  return (
    <div style={{ minHeight: "100vh" }}>
      <Toaster position="top-center" theme="dark" />
      <div className="container section-padding" style={{ maxWidth: "900px" }}>
        {!sessionId && (
          <div style={{ textAlign: "center" }}>
            <div className="hero-kicker">No session</div>
            <h1 style={{ fontSize: "2.4rem" }}>We could not find your order.</h1>
            <button onClick={() => navigate("/")} className="btn btn-primary-gold" style={{ marginTop: "32px" }}>
              Back to home
            </button>
          </div>
        )}

        {sessionId && isFailed && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div className="hero-kicker">Payment unsuccessful</div>
            <h1 style={{ fontSize: "2.4rem", marginBottom: "16px" }}>Your payment did not go through.</h1>
            <p style={{ color: "var(--text-secondary)", fontSize: "1.05rem" }}>
              No charges were made. Please try again or use a different payment method.
            </p>
            <button onClick={() => navigate("/checkout")} className="btn btn-primary-gold" style={{ marginTop: "32px" }}>
              Try again
            </button>
            <button onClick={() => navigate("/")} className="btn btn-outline" style={{ marginTop: "12px", marginLeft: "12px" }}>
              ← Back to home
            </button>
          </div>
        )}

        {sessionId && !isPaid && !isFailed && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div className="hero-kicker">Confirming payment</div>
            <h1 style={{ fontSize: "2.4rem" }}>One moment.</h1>
            <p style={{ marginTop: "16px", color: "var(--text-secondary)", fontSize: "1.05rem" }}>
              We are verifying your payment.
            </p>
            <div style={{ marginTop: "40px", width: "40px", height: "40px", margin: "40px auto 0", border: "2px solid rgba(201,162,39,0.3)", borderTopColor: "var(--gold-warm)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
          </div>
        )}

        {sessionId && isPaid && (
          <>
            <div className="hero-kicker" style={{ marginBottom: "16px" }}>Welcome to the Collection</div>
            <h1 data-testid={LANDING.thanksTitle} style={{ fontSize: "3rem", marginBottom: "24px" }}>
              You are in.<br /><span style={{ fontStyle: "italic", color: "var(--gold-warm)" }}>Now the work begins.</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: "var(--text-secondary)", lineHeight: 1.8, maxWidth: "600px" }}>
              Your download links are below. We also sent them to{" "}
              <span style={{ color: "var(--gold-warm)" }}>{state.email || "your email"}</span>.
              Save them. They never expire.
            </p>

            <div style={{ marginTop: "40px", borderTop: "1px solid var(--border-glass)" }}>
              {state.downloads.map((d) => (
                <a
                  key={d.id}
                  href={d.url || "#"}
                  target={d.url ? "_blank" : undefined}
                  rel={d.url ? "noopener noreferrer" : undefined}
                  data-testid={LANDING.thanksDownload(d.id)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px", padding: "20px 0", borderBottom: "1px solid var(--border-glass)", textDecoration: "none", color: "inherit", transition: "color 0.2s", opacity: d.url ? 1 : 0.6 }}
                  onClick={(e) => { if (!d.url) e.preventDefault(); }}
                >
                  <div>
                    <p style={{ fontFamily: "var(--font-serif)", fontSize: "1.4rem", fontWeight: 600 }}>{d.title}</p>
                    <p style={{ fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: "4px" }}>PDF · Instant download</p>
                  </div>
                  <span style={{ color: "var(--gold-warm)", fontWeight: 600 }}>
                    {d.url ? "Download →" : "Sent by email"}
                  </span>
                </a>
              ))}
            </div>

            <div style={{ marginTop: "48px" }}>
              <button onClick={() => navigate("/")} className="btn btn-outline">
                ← Back to home
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
